import { useQuery } from "@tanstack/react-query";
import { useTRPC } from "@/trpc";
import { Button } from "@/components/ui/button";
import { ScreeningCard } from "./screening-card";
import {
  ACTIVE_SCREENING_POLL_MS,
  isActiveScreening,
  type ReportIssue,
  type ScreeningScope,
} from "./types";

type Props = ScreeningScope & {
  open: boolean;
  checkId?: string;
  /** Narrows a shared check (such as the state Medicaid exclusion check) to
   * one of its results, e.g. a single state. */
  resultKey?: string;
  /** Issues of the run report item these screenings belong to. */
  issues?: ReportIssue[];
};

export function ProviderScreenings({ open, ...props }: Props) {
  if (!open) {
    return null;
  }
  return (
    <Screenings
      key={`${props.organizationBusinessUnitId}:${props.providerUserId}:${props.credentialViewId ?? props.expectedAffiliationId ?? "current"}`}
      {...props}
    />
  );
}

function Screenings({
  checkId,
  resultKey,
  issues,
  ...scope
}: Omit<Props, "open">) {
  const trpc = useTRPC();
  const queryOptions = trpc.psv.getProviderScreenings.queryOptions(scope);
  const query = useQuery({
    ...queryOptions,
    refetchInterval: (state) => {
      const rows = state.state.data?.screenings;
      return rows?.some((row) => isActiveScreening(row.latest))
        ? ACTIVE_SCREENING_POLL_MS
        : false;
    },
  });
  const content = (
    <>
      {query.isLoading && (
        <p role="status" className="text-xs text-muted-foreground">
          Loading provider screenings…
        </p>
      )}
      {query.isError && (
        <div role="alert" className="space-y-1 text-xs text-destructive">
          <p>Couldn&apos;t load the latest provider screenings.</p>
          <Button
            type="button"
            variant="link"
            size="sm"
            className="h-auto p-0"
            disabled={query.isFetching}
            onClick={() => void query.refetch()}
          >
            Retry screenings
          </Button>
        </div>
      )}
      {query.data?.screenings
        .filter(
          (screening) =>
            (!scope.runId || screening.latest?.runId === scope.runId) &&
            (!checkId || screening.latest?.checkId === checkId) &&
            (!resultKey || screening.resultKey === resultKey),
        )
        .map((screening) => (
          <ScreeningCard
            key={screening.key}
            screening={
              scope.runId ? { ...screening, imported: null } : screening
            }
            scope={scope}
            embedded={Boolean(checkId)}
            issues={issues}
          />
        ))}
    </>
  );
  if (checkId) {
    return <div className="space-y-3">{content}</div>;
  }
  return (
    <section
      aria-label="Provider screenings"
      className="space-y-3 rounded-lg border bg-background p-4"
    >
      <div>
        <h3 className="text-sm font-semibold">Provider screenings</h3>
        <p className="mt-1 text-xs text-muted-foreground">
          {scope.runId
            ? "Exclusion and sanctions results for this verification run."
            : "Latest exclusion and sanctions results."}
        </p>
      </div>
      {content}
    </section>
  );
}
